import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatTL, todayISO } from "@/lib/format";
import { PageHeader, AddButton, Field, inputCls } from "@/components/shared";
import { usePermission } from "@/lib/permissions";
import FilterBar from "@/components/FilterBar";
import { downloadCSV, fmtNum } from "@/lib/csv";
import { Trash2, Pencil, X, Repeat, PlusCircle, Power } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Kira", "Personel", "Fatura", "Vergi", "Malzeme", "Pazarlama", "Ulaşım", "Diğer"];

const emptyForm = {
  title: "", amount: "", category: "Kira", day_of_month: 1, active: true, note: "",
};

export default function RecurringExpenses() {
  const canEdit = usePermission("recurring", "edit");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/recurring-expenses");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (x) => { setEditing(x); setForm({ ...x, amount: String(x.amount) }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) || 0, day_of_month: parseInt(form.day_of_month) };
    try {
      if (editing) { await api.put(`/recurring-expenses/${editing.id}`, payload); toast.success("Güncellendi"); }
      else { await api.post("/recurring-expenses", payload); toast.success("Eklendi"); }
      setOpen(false); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/recurring-expenses/${id}`);
    toast.success("Silindi"); load();
  };

  const toggle = async (item) => {
    await api.put(`/recurring-expenses/${item.id}`, { ...item, active: !item.active });
    load();
  };

  const generateNow = async (id) => {
    await api.post(`/recurring-expenses/${id}/generate`);
    toast.success("Gider oluşturuldu");
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((x) => {
      if (q && !`${x.title} ${x.category} ${x.note || ""}`.toLowerCase().includes(q)) return false;
      if (status === "active" && !x.active) return false;
      if (status === "inactive" && x.active) return false;
      return true;
    });
  }, [items, search, status]);

  const exportCSV = () => {
    downloadCSV(filtered, [
      { label: "Başlık", key: "title" },
      { label: "Kategori", key: "category" },
      { label: "Ayın Günü", key: "day_of_month" },
      { label: "Tutar (TL)", get: (r) => fmtNum(r.amount) },
      { label: "Durum", get: (r) => (r.active ? "Aktif" : "Pasif") },
      { label: "Not", key: "note" },
    ], `tekrarlayan_giderler_${todayISO()}.csv`);
  };

  const clearFilter = () => { setSearch(""); setStatus(""); };

  return (
    <div className="p-6 md:p-10 lg:p-12 fade-in max-w-[1400px]">
      <PageHeader
        eyebrow="Otomatik takip"
        title="Tekrarlayan Giderler"
        description="Kira, personel maaşı gibi düzenli giderlerinizi tanımlayın; yaklaşan ödemelerde otomatik görünsün."
        action={canEdit ? <AddButton onClick={openAdd} label="Yeni Tekrarlayan Gider" testid="add-recurring-btn" /> : null}
      />

      <FilterBar
        search={search} onSearch={setSearch}
        status={status} onStatus={setStatus}
        statusOptions={[{ value: "active", label: "Aktif" }, { value: "inactive", label: "Pasif" }]}
        dateFrom="" onDateFrom={() => {}}
        dateTo="" onDateTo={() => {}}
        onExport={exportCSV}
        onClear={clearFilter}
        searchPlaceholder="Başlık, kategori, not…"
        testidPrefix="recurring-filter"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-[#E6E4DD] rounded-2xl p-12 text-center">
            <Repeat size={40} className="mx-auto mb-3 text-[#5C5B57]" strokeWidth={1.5} />
            <p className="text-[#5C5B57]">Henüz tekrarlayan gider yok.</p>
          </div>
        )}
        {filtered.map((x) => (
          <div key={x.id} className={`bg-white border rounded-2xl p-6 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-shadow ${x.active ? "border-[#E6E4DD]" : "border-[#E6E4DD] opacity-60"}`} data-testid={`recurring-card-${x.id}`}>
            <div className="flex items-start justify-between mb-4">
              <span className="inline-block text-xs px-2.5 py-1 rounded-full font-medium bg-[#F0EFEA] text-[#5C5B57]">{x.category}</span>
              {canEdit && (
              <div className="flex gap-1">
                <button onClick={() => toggle(x)} data-testid={`toggle-recurring-${x.id}`} className={`p-2 rounded-lg hover:bg-[#F0EFEA] ${x.active ? "text-[#4A6741]" : "text-[#5C5B57]"}`} title={x.active ? "Devre dışı bırak" : "Aktif et"}>
                  <Power size={16} />
                </button>
                <button onClick={() => openEdit(x)} data-testid={`edit-recurring-${x.id}`} className="p-2 rounded-lg hover:bg-[#F0EFEA] text-[#5C5B57]"><Pencil size={16} /></button>
                <button onClick={() => remove(x.id)} data-testid={`delete-recurring-${x.id}`} className="p-2 rounded-lg hover:bg-[#B24C4C]/10 text-[#B24C4C]"><Trash2 size={16} /></button>
              </div>
              )}
            </div>
            <p className="font-heading text-xl text-[#2D2C2A] mb-2">{x.title}</p>
            <p className="text-sm text-[#5C5B57] mb-4">Her ayın {x.day_of_month}. günü</p>
            <p className="font-heading text-2xl text-[#B24C4C] mb-4">-{formatTL(x.amount)}</p>
            <button
              onClick={() => generateNow(x.id)}
              data-testid={`generate-recurring-${x.id}`}
              disabled={!canEdit}
              className="w-full inline-flex items-center justify-center gap-2 border border-[#8A5A44] text-[#8A5A44] hover:bg-[#8A5A44] hover:text-white rounded-xl px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <PlusCircle size={16} strokeWidth={1.75} /> Bugün Gider Olarak Kaydet
            </button>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="recurring-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl text-[#2D2C2A]">{editing ? "Düzenle" : "Yeni Tekrarlayan Gider"}</h3>
              <button onClick={() => setOpen(false)} className="text-[#5C5B57]"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Başlık"><input required className={inputCls} data-testid="recurring-title-input" placeholder="Örn: Ofis Kirası" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tutar (₺)"><input required type="number" step="0.01" min="0" className={inputCls} data-testid="recurring-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
                <Field label="Ayın Günü (1-28)"><input required type="number" min="1" max="28" className={inputCls} data-testid="recurring-day-input" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} /></Field>
              </div>
              <Field label="Kategori">
                <select className={inputCls} data-testid="recurring-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Not"><textarea rows={2} className={inputCls} data-testid="recurring-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
              <label className="flex items-center gap-2 text-sm text-[#2D2C2A]">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="recurring-active-check" />
                Aktif (yaklaşan ödemelerde görünsün)
              </label>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-[#E6E4DD] rounded-xl px-6 py-3 hover:bg-[#F0EFEA]">İptal</button>
                <button type="submit" data-testid="recurring-submit" className="flex-1 bg-[#8A5A44] text-white hover:bg-[#714835] rounded-xl px-6 py-3 font-medium">{editing ? "Güncelle" : "Ekle"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
